import useUpsertCollectionMutation from './useUpsertCollectionMutation';
import {
  CollectionReviewStates,
  CollectionType,
  CollectionUnion,
} from '../../../api/collections';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDeviceType from '../../../hooks/useDeviceType';
import useCollectionSubmitValidation from './useCollectionSubmitValidation';
import usePublishCollection from './usePublishCollection';

const useCollectionCard = (
  collection: CollectionUnion,
  handleDeleteCollection?: (collectionId: string) => void,
) => {
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();

  const { mutate: updateCollectionMutation } = useUpsertCollectionMutation();

  const [
    profileIncompleteErrorDialogOpen,
    setProfileIncompleteErrorDialogOpen,
  ] = useState<boolean>(false);

  const {
    warningDialogOpen,
    isNameTaken,
    handlePublishClick,
    handleWarningConfirm,
    handleWarningClose,
    warningMessage,
    nameWarningNote,
  } = usePublishCollection(collection.id, collection.name ?? '', {
    onBeforeOpen: () => {
      if (isProfileMissingFields) {
        setProfileIncompleteErrorDialogOpen(true);
        return false;
      }
      return true;
    },
    // Without the stored reply the api replaces the whole reviewStatus, clearing
    // both it and the reviewer's note.
    comment: collection.reviewStatus?.comment,
  });

  const onClickCard = (collection: CollectionUnion) => {
    if (isMobile) return;
    if (
      collection.type === CollectionType.Draft &&
      collection.reviewStatus.state === CollectionReviewStates.Draft
    ) {
      navigate(`/collection/edit/${collection.id}`);
    } else {
      navigate(`/collection/${collection.id}`);
    }
  };

  const handleEditCollection = () => {
    navigate(`/collection/edit/${collection.id}`);
  };

  const handleCancelReview = () => {
    if (collection.type !== CollectionType.Draft) {
      return;
    }
    updateCollectionMutation({
      collectionId: collection.id,
      collection: {
        // State only: withdrawing must not destroy the review.
        reviewStatus: { state: CollectionReviewStates.Draft },
      },
    });
  };

  const isButtonDisabled =
    (collection.type === CollectionType.Draft &&
      (collection?.reviewStatus?.state === CollectionReviewStates.Approved ||
        collection?.reviewStatus?.state === CollectionReviewStates.Pending)) ||
    collection.type === CollectionType.Approved;

  const collectionData = useMemo(
    () => ({
      name: collection.name,
      coverUrl: collection.coverUrl,
      logoUrl: collection.logoUrl,
      klydoIds: collection.klydoIds,
      description: collection.description,
      productPageMetadata: collection.productPageMetadata,
    }),
    [collection],
  );

  const {
    isSubmitDisabled: isValidationDisabled,
    handleMissingFields: missingFieldsTooltip,
    isProfileMissingFields,
  } = useCollectionSubmitValidation(
    collectionData,
    false,
    false,
    collection.klydos,
  );

  const isSubmitDisabled =
    isValidationDisabled || collection.type === CollectionType.Approved;

  const menuButtonItemsList = useMemo(() => {
    return [
      collection.type === CollectionType.Draft &&
      collection?.reviewStatus?.state === CollectionReviewStates.Pending
        ? {
            title: 'Cancel Review',
            isDisabled: false,
            onClick: () => handleCancelReview(),
          }
        : {
            title: 'Marketplace',
            isDisabled: isSubmitDisabled,
            onClick: () => handlePublishClick(),
            tooltip: missingFieldsTooltip,
          },
      {
        title: 'Edit Collection',
        isDisabled: isButtonDisabled,
        onClick: () => handleEditCollection(),
      },
      {
        title: 'Delete',
        isDisabled: isButtonDisabled,
        onClick: () => handleDeleteCollection?.(collection.id),
      },
    ];
  }, [
    collection,
    handlePublishClick,
    isButtonDisabled,
    isSubmitDisabled,
    missingFieldsTooltip,
  ]);

  return {
    menuButtonItemsList,
    onClickCard,
    warningDialogOpen,
    isNameTaken,
    handleWarningConfirm,
    handleWarningClose,
    warningMessage,
    nameWarningNote,
    profileIncompleteErrorDialogOpen,
    setProfileIncompleteErrorDialogOpen,
  };
};

export default useCollectionCard;
